import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useGetHolidayPayBalanceSummaryList } from '@nxsys/hooks/api/holidayPay';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetHolidayPayBalanceSummaryListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import {
  formatFullName,
  getTaxWeekTaxYear,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { ExportToCsv } from 'export-to-csv';
import { debounce } from 'lodash';
import HolidayPaySummaryFilterModal, {
  HolidayPaySummaryFilterValues,
} from '../components/HolidayPaySummaryFilterModal';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHolidayPayBalanceSummaryColumns } from './columns';


const HolidayPayBalanceSummaryReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch();
  const { payrollCountry, isBureau } = useSelector((state: AppState) => state.appStore);
  const { holidayPayBalanceSummaryList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } =
    holidayPayBalanceSummaryList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<IHolidayPayBalanceSummary[]>(
    [],
  );
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const {
    loading: loadingHolidayPaySummaryList,
    run: getHolidayPayBalanceSummaryList,
  } = useGetHolidayPayBalanceSummaryList();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.holidayPayBalanceSummary']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetHolidayPayBalanceSummaryListParams) => {
      const newParams = {
        ...params,
      };

      dispatch(reportActions.getHolidayPayBalanceSummaryList(newParams));
    },
    [dispatch],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: HolidayPaySummaryFilterValues) => {
    const params: GetHolidayPayBalanceSummaryListParams = {
      ...queryParams,
      agencyId: values.agency?.value,
      companyId: values.company?.value,
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleExport = (data: IHolidayPayBalanceSummary[]) => {
    const dataExport = data.map((item) => {
      const commonRow = {
        id: item?.id || '',
        agencyRef: isBureau
          ? item?.agencyRef || item.systemId || ''
          : `${item?.primaryAgency?.agencyPrefix || '___'}/${
              item?.agencyRef || item?.systemId || '_'
            }`,
        name: formatFullName(item),
        companyName: item?.companyPayment?.companyName || '',
        ...(isBureau ? {} : { agencyName: item?.primaryAgency?.name || '' }),
        // NX-5114: use BE-supplied `balance` (rounded via HolidayPay.afterLoad,
        // NX-4390) instead of recomputing raw float4. See
        // report.payroll.service.ts after getManyAndCount.
        balance:
          formatMoney(
            item?.latestHolidayPay?.balance ?? 0,
            showCurrencyByCountry(payrollCountry),
          ) || '',
      };
      if (isBureau) {
        return {
          ...commonRow,
          openingBalance:
            formatMoney(
              item?.latestHolidayPay?.openingBalance,
              showCurrencyByCountry(payrollCountry),
            ) || '',
          accrued:
            formatMoney(
              item?.latestHolidayPay?.accrued,
              showCurrencyByCountry(payrollCountry),
            ) || '',
          paid:
            formatMoney(
              item?.latestHolidayPay?.paid,
              showCurrencyByCountry(payrollCountry),
            ) || '',
        };
      }
      return {
        ...commonRow,
        ERNI:
          formatMoney(
            item?.latestHolidayPay?.ERNI,
            showCurrencyByCountry(payrollCountry),
          ) || '',
        appLevy:
          formatMoney(
            item?.latestHolidayPay?.appLevy,
            showCurrencyByCountry(payrollCountry),
          ) || '',
        ERPension:
          formatMoney(
            item?.latestHolidayPay?.ERPension,
            showCurrencyByCountry(payrollCountry),
          ) || '',
      };
    });
    const headers = [
      'Employee ID',
      isBureau ? 'Employer Ref' : 'Agency Ref',
      'Name',
      isBureau ? 'Employer' : 'Payment Company',
      ...(isBureau ? [] : ['Primary Agency']),
      'Holiday pay balance',
      ...(isBureau
        ? ['Opening Balance', 'Accrued', 'Paid']
        : ['ERNI', 'App Levy', 'Empr Pension']),
    ];
    const csvExporter = new ExportToCsv({
      filename: `HP-summary-export-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getHolidayPayBalanceSummaryList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.agencyId ||
    !!queryParams?.companyId;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loadingHolidayPaySummaryList}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
              <AppCheckbox
                checked={!!queryParams?.excludeLeavers}
                onChange={(e) =>
                  handleFetchData({
                    ...queryParams,
                    page: 0,
                    excludeLeavers: e.target.checked,
                  })
                }
              >
                {messages['common.excludeLeavers']}
              </AppCheckbox>
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2000, y: 700 }}
            columns={getHolidayPayBalanceSummaryColumns({
              messages,
              payrollCountry,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <HolidayPaySummaryFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
        isBureau={isBureau}
      />
    </AppsContainer>
  );
};

export default HolidayPayBalanceSummaryReport;
